import React, { useState, useEffect } from 'react'
import { useSummaryFormContext } from '../hook/summary-form'
import { Box, Paper, Text, Divider, SimpleGrid, Select, NumberInput, Stack, Button, TextInput } from '@mantine/core'
import { getAllCurrencyList } from '../../../../../../services/currency'
import { reMarkupProduct } from '../../../../../../services/products'
import { notificationError, notificationSuccess } from '../../../../../ui/Notifications'
import { useParams } from 'react-router-dom'
import { quantityUnit, dimensionUnit } from '../../../../../../helper/master-unit'

const weightUnitList = [
  {
    label: 'Kg',
    value: 'kg',
  },
  {
    label: 'gr',
    value: 'gr',
  },
  {
    label: 'lb',
    value: 'lb',
  },
  {
    label: 'oz',
    value: 'oz',
  },
]

const SummaryDetailProduct = ({ reloadProduct }) => {
  const params = useParams()
  const productId = params.product_id
  const form = useSummaryFormContext()
  // currency
  const [currencyList, setCurrencyList] = useState([])
  // service
  const [loadingRemarkupPrice, setLoadingRemarkupPrice] = useState(false)

  const handleGetListCurrency = async () => {
    const parameter = {
      groupBy: 'currency_code',
    }
    try {
      const response = await getAllCurrencyList(parameter)
      const dataCurrency = response.data
      const remapDataCurrency = dataCurrency.map((val) => val.attributes.currency_code)
      setCurrencyList(remapDataCurrency)
    } catch (error) {
      console.log(error)
    }
  }

  const handleRemarkupProduct = async (valueMarkupPercentage) => {
    setLoadingRemarkupPrice(true)
    const payload = {
      markup_price: valueMarkupPercentage
    }
    try {
      const response = await reMarkupProduct(productId, payload)
      if (response.success) {
        notificationSuccess('Remarkup Product Success', 'You have successfully updated remarkup product price')
        reloadProduct()
      }
    } catch (error) {
      const errorMessage = error.response.data.message
      notificationError('Remarkup Product Error', `${errorMessage ? errorMessage : 'Please contact your administrator'}`)
      const errorMessageForm = error.response.data.errors
      if (errorMessageForm) {
        Object.keys(errorMessageForm).forEach((key) => {
          form.setFieldError(key, errorMessageForm[key][0])
        })
      }
    } finally {
      setLoadingRemarkupPrice(false)
    }
  }

  useEffect(() => {
    handleGetListCurrency()
  }, [])

  form.watch('group_qty', ({ value }) => {
    if (value > 0) {
      form.setFieldValue('minimum_qty', value)
    } else if (value === 0 || value === '') {
      form.setFieldValue('minimum_qty', 1)
    }
  })

  const mappingFieldDimensions = () => {
    const remapForm = form.values.dimensions.map((_, index) => {
      return (
        <NumberInput
          label={form.values.dimensions[index].label}
          tt='capitalize'
          rightSection={<Text fz={13} c='black' tt='lowercase'>{form.values.dimension_unit}</Text>}
          rightSectionWidth={40}
          key={form.key(`dimensions.${index}.value`)}
          {...form.getInputProps(`dimensions.${index}.value`)}
        />
      )
    })
    return remapForm
  }

  return (
    <Paper my={'xl'} p={'md'} withBorder shadow="xs" radius="md">
      <Text fw={500}>
        Product Detail
      </Text>

      <Divider my={10} />

      <Box style={{ margin: '20px 0' }}>
        <Text fw={500} style={{ marginBottom: '10px'}}>
          Price {form.values.original_currency}
        </Text>

        <SimpleGrid cols={4} spacing="xl" verticalSpacing="xl">
          <Select
            name='currency'
            label="Original Currency"
            data={currencyList}
            key={form.key('original_currency')}
            {...form.getInputProps('original_currency')}
            nothingFoundMessage={currencyList.length > 0 ? '' : 'No Currency Found'}
          />
          <NumberInput
            label="Retail Price"
            placeholder='Input price'
            leftSection={<Text fz={12} c='black' tt='capitalize'>{form.values.original_currency}</Text>}
            leftSectionWidth={40}
            rightSection={<Text fz={12} c='black' tt='capitalize'> /{form.values.quantity_unit}</Text>}
            rightSectionWidth={60}
            key={form.key('regular_price')}
            {...form.getInputProps('regular_price')}
            thousandSeparator=","
            withAsterisk
          />
          <NumberInput
            label="Sale Price"
            placeholder='Input price'
            leftSection={<Text fz={12} c='black' tt='capitalize'>{form.values.original_currency}</Text>}
            leftSectionWidth={40}
            rightSection={<Text fz={12} c='black' tt='capitalize'> /{form.values.quantity_unit}</Text>}
            rightSectionWidth={60}
            key={form.key('sale_price')}
            {...form.getInputProps('sale_price')}
            thousandSeparator=","
            withAsterisk
          />
          <NumberInput
            label="Dropship Price"
            placeholder='Input price'
            leftSection={<Text fz={12} c='black' tt='capitalize'>{form.values.original_currency}</Text>}
            leftSectionWidth={40}
            rightSection={<Text fz={12} c='black' tt='capitalize'> /{form.values.quantity_unit}</Text>}
            rightSectionWidth={60}
            key={form.key('reseller_price')}
            {...form.getInputProps('reseller_price')}
            thousandSeparator=","
            withAsterisk
          />
          <Stack>
            <NumberInput
              label="Markup Percentage"
              rightSection={<Text fz={12} c='black' tt='capitalize'>%</Text>}
              rightSectionWidth={40}
              key={form.key('markup_percentage')}
              {...form.getInputProps('markup_percentage')}
              min={0}
              max={100}
              clampBehavior="strict"
            />
            <Box>
              <Button size='xs' loading={loadingRemarkupPrice} onClick={form.onSubmit((value) => handleRemarkupProduct(value.markup_percentage))}>Re Mark-up</Button>
            </Box>
          </Stack>
        </SimpleGrid>
      </Box>

      <Divider my={20} />

      <Box style={{ margin: '20px 0' }}>
        <Text fw={500} style={{ marginBottom: '10px'}}>
          Stock
        </Text>

        <SimpleGrid cols={4} spacing="xl" verticalSpacing="xl">
          <Select
            name='quantity-unit'
            label='Quantity Unit'
            data={quantityUnit}
            key={form.key('quantity_unit')}
            {...form.getInputProps('quantity_unit')}
            clearable
            allowDeselect={false}
          />
          <NumberInput
            label="Quantity"
            rightSection={<Text fz={12} c='black' tt='capitalize'>{form.values.quantity_unit}</Text>}
            rightSectionWidth={50}
            key={form.key('quantity')}
            {...form.getInputProps('quantity')}
          />
          <NumberInput
            label="Min order Qty"
            rightSection={<Text fz={12} c='black' tt='capitalize'>{form.values.quantity_unit}</Text>}
            rightSectionWidth={50}
            key={form.key('minimum_qty')}
            {...form.getInputProps('minimum_qty')}
            disabled={form.getValues().group_qty > 0}
          />
          <NumberInput
            label="Qty Group / Qty Case"
            description="If sold in cases, the minimum quantity will follow the case value"
            rightSection={<Text fz={12} c='black' tt='capitalize'>{form.values.quantity_unit}</Text>}
            rightSectionWidth={50}
            key={form.key('group_qty')}
            {...form.getInputProps('group_qty')}
            inputWrapperOrder={['label', 'input', 'description', 'error']}
          />
        </SimpleGrid>
      </Box>

      <Divider my={20} />

      <Box style={{ margin: '20px 0' }}>
        <Text fw={500} style={{ marginBottom: '10px'}}>
          SKU Information
        </Text>

        <SimpleGrid cols={4} spacing="xl" verticalSpacing="xl">
          <NumberInput
            label="Quantity per CBM"
            rightSection={<Text fz={12} c='black' tt='capitalize'>{form.values.quantity_unit}/CBM</Text>}
            rightSectionWidth={80}
            key={form.key('qty_per_cbm')}
            {...form.getInputProps('qty_per_cbm')}
          />
          <TextInput
            label="Original SKU"
            placeholder="Original SKU"
            key={form.key('sku')}
            {...form.getInputProps('sku')}
          />
          <TextInput
            label="ALI SKU"
            placeholder="Input placeholder"
            key={form.key('ali_sku')}
            {...form.getInputProps('ali_sku')}
          />
        </SimpleGrid>
      </Box>

      <Divider my={20} />

      <Box style={{ margin: '20px 0' }}>
        <Text fw={500} style={{ marginBottom: '10px'}}>
          Weight
        </Text>

        <SimpleGrid cols={4} spacing="xl" verticalSpacing="xl">
          <Select
            name='weight-unit'
            label='Unit Weight'
            data={weightUnitList}
            key={form.key('weight_unit')}
            {...form.getInputProps('weight_unit')}
            allowDeselect={false}
          />
          <NumberInput
            label={`Weight (${form.values.weight_unit})`}
            rightSection={<Text fz={12} c='black'>{form.values.weight_unit}/{form.values.quantity_unit}</Text>}
            rightSectionWidth={65}
            key={form.key('weight')}
            {...form.getInputProps('weight')}
          />
        </SimpleGrid>
      </Box>

      <Divider my={20} />

      <Box style={{ margin: '20px 0' }}>
        <Text fw={500} style={{ marginBottom: '10px'}}>
          Dimensions
        </Text>

        <SimpleGrid cols={4} spacing={'xl'} verticalSpacing={'xl'}>
          <Select
            name='dimesion-unit'
            label='Unit Dimension'
            data={dimensionUnit}
            key={form.key('dimension_unit')}
            {...form.getInputProps('dimension_unit')}
            allowDeselect={false}
          />

          {mappingFieldDimensions()}
        </SimpleGrid>
      </Box>
    </Paper>
  )
}

export default SummaryDetailProduct